import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Label } from '@shared/components/ui/label'
import { Input } from '@shared/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { Checkbox } from '@shared/components/ui/checkbox'
import { toast } from '@shared/hooks/use-toast'
import { PageHeader } from '@shared/components/ui/page-header'
import { SubscriptionManagement } from '@/components/SubscriptionManagement'
import { TenantAnalyticsDashboard } from '@/components/TenantAnalyticsDashboard'
import { authFetch } from '@/lib/auth-fetch'
import { apiUrl } from '@/lib/api'

type TenantDetails = {
  id: string
  name: string
  slug: string
  status: 'active' | 'suspended'
  created_at: string
  updated_at: string
  plan_id: string | null
  plan_name?: string | null
  users_count?: string
  roles_count?: string
}

type Plan = { id: string; name: string }

type FeaturesPayload = {
  defaults: Record<string, boolean>
  overrides: Record<string, boolean>
  effective: Record<string, boolean>
  planId: string | null
}

export default function TenantDetailsPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()

  const [tenant, setTenant] = useState<TenantDetails | null>(null)
  const [plans, setPlans] = useState<Plan[]>([])
  const [planId, setPlanId] = useState<string>('')
  const [featureDefaults, setFeatureDefaults] = useState<Record<string, boolean>>({})
  const [featureOverrides, setFeatureOverrides] = useState<Record<string, boolean>>({})
  const [featureLimits, setFeatureLimits] = useState<Record<string, Record<string, any>>>({})
  const [featureEffective, setFeatureEffective] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [savingPlan, setSavingPlan] = useState(false)
  const [savingFeatures, setSavingFeatures] = useState(false)
  const [settings, setSettings] = useState<Record<string, any>>({})
  const [domains, setDomains] = useState<Array<{ id: string; domain: string; is_primary: boolean; is_verified: boolean; created_at: string }>>([])
  const [storageUsage, setStorageUsage] = useState<{ total_storage_bytes: number; total_files_count: number; updated_at: string | null }>({ total_storage_bytes: 0, total_files_count: 0, updated_at: null })

  const [featureRegistry, setFeatureRegistry] = useState<Array<{ key: string; name: string; category: string; limitsSchema?: Record<string, { type: string; description?: string }> }>>([])
  const featureList = useMemo(() => featureRegistry.map(f => f.key), [featureRegistry])

  const load = async () => {
    if (!tenantId) return
    setLoading(true)
    try {
      // Tenant details
      const tRes = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}`))
      if (!tRes.ok) throw new Error('Failed to load tenant')
      const tJson: TenantDetails = await tRes.json()
      setTenant(tJson)
      setPlanId(tJson.plan_id || '')

      // Plans
      const pRes = await authFetch(apiUrl(`/api/admin/plans`))
      if (!pRes.ok) throw new Error('Failed to load plans')
      const pJson = await pRes.json()
      setPlans(pJson.plans || [])

      // Features (defaults + overrides + effective)
      if (tJson.plan_id) {
        const fRes = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/plan-features?includeDefaults=1`))
        if (!fRes.ok) throw new Error('Failed to load features')
        const fJson: any = await fRes.json()
        setFeatureDefaults(fJson.defaults || {})
        setFeatureOverrides(fJson.overrides || {})
        setFeatureEffective(fJson.effective || {})
        setFeatureLimits(fJson.limits || {})
      } else {
        setFeatureDefaults({})
        setFeatureOverrides({})
        setFeatureEffective({})
        setFeatureLimits({})
      }

      // Feature registry for labels
      const rRes = await authFetch(apiUrl(`/api/admin/features/registry`))
      if (rRes.ok) {
        const rJson = await rRes.json()
        setFeatureRegistry((rJson.features || []).map((f: any) => ({ key: f.key, name: f.name, category: f.category, limitsSchema: f.limitsSchema || {} })))
      }

      // Settings
      const sRes = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/settings`))
      if (sRes.ok) {
        const sJson = await sRes.json()
        const obj: Record<string, any> = {}
          ; (sJson.settings || []).forEach((row: any) => { obj[row.key] = row.value })
        setSettings(obj)
      }

      // Domains
      const dRes = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/domains`))
      if (dRes.ok) {
        const dJson = await dRes.json()
        setDomains(dJson.domains || [])
      }

      // Storage usage
      const suRes = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/storage-usage`))
      if (suRes.ok) {
        const suJson = await suRes.json()
        setStorageUsage({
          total_storage_bytes: Number(suJson.total_storage_bytes || 0),
          total_files_count: Number(suJson.total_files_count || 0),
          updated_at: suJson.updated_at || null,
        })
      }

    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load tenant details', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [tenantId])

  const savePlan = async () => {
    if (!tenantId || !planId) {
      toast({ title: 'Select a plan', description: 'Please select a plan to assign', variant: 'destructive' })
      return
    }
    setSavingPlan(true)
    try {
      const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/plan`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ planId }) })
      if (!res.ok) throw new Error('Failed to assign plan')
      toast({ title: 'Plan assigned' })
      await load()
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to assign plan', variant: 'destructive' })
    } finally {
      setSavingPlan(false)
    }
  }

  const saveFeatures = async () => {
    if (!tenantId) return
    if (!planId) {
      toast({ title: 'Assign a plan', description: 'Please assign a plan before saving features', variant: 'destructive' })
      return
    }
    setSavingFeatures(true)
    try {
      const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/plan-features`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ features: featureOverrides, limits: featureLimits }) })
      if (!res.ok) throw new Error('Failed to save features')
      toast({ title: 'Features saved' })
      await load()
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to save features', variant: 'destructive' })
    } finally {
      setSavingFeatures(false)
    }
  }

  const resetFeatures = async (keys?: string[]) => {
    if (!tenantId) return
    const qs = keys && keys.length ? `?keys=${encodeURIComponent(keys.join(','))}` : ''
    const { auth } = await import('@/lib/firebase')
    const idToken2 = await auth.currentUser?.getIdToken()
    const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/plan-features${qs}`), { method: 'DELETE', headers: idToken2 ? { 'Authorization': `Bearer ${idToken2}` } : {} })
    if (!res.ok) {
      toast({ title: 'Error', description: 'Failed to reset features', variant: 'destructive' })
      return
    }
    toast({ title: 'Features reset to defaults' })
    await load()
  }

  const toggleStatus = async () => {
    if (!tenantId || !tenant) return
    const newStatus = tenant.status === 'active' ? 'suspended' : 'active'
    try {
      const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/status`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) })
      if (!res.ok) throw new Error('Failed to update status')
      toast({ title: 'Status updated' })
      await load()
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to update status', variant: 'destructive' })
    }
  }

  const saveSettings = async () => {
    if (!tenantId) return
    try {
      const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/settings`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings) })
      if (!res.ok) throw new Error('Failed to save settings')
      toast({ title: 'Settings saved' })
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to save settings', variant: 'destructive' })
    }
  }

  const addDomain = async (domain: string, isPrimary?: boolean) => {
    if (!tenantId || !domain) return
    try {
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/domains`), {
        method: 'POST', 
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        }, 
        body: JSON.stringify({ domain, isPrimary })
      })
      if (!res.ok) throw new Error('Failed to add domain')
      await load()
      toast({ title: 'Domain added' })
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to add domain', variant: 'destructive' })
    }
  }

  const setPrimaryDomain = async (domainId: string) => {
    if (!tenantId) return
    try {
      const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/domains/${domainId}/primary`), { method: 'POST' })
      if (!res.ok) throw new Error('Failed to set primary domain')
      await load()
      toast({ title: 'Primary domain set' })
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to set primary', variant: 'destructive' })
    }
  }

  const deleteDomain = async (domainId: string) => {
    if (!tenantId) return
    try {
      const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/domains/${domainId}`), { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete domain')
      await load()
      toast({ title: 'Domain deleted' })
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to delete domain', variant: 'destructive' })
    }
  }

  const handleSubscriptionUpdated = async () => {
    // Reload tenant details (includes plan information)
    if (!tenantId) return
    try {
      const tenantRes = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}`))
      if (tenantRes.ok) {
        const tenantData = await tenantRes.json()
        setTenant(tenantData)
      }
    } catch (error) {
      console.error('Failed to reload tenant data:', error)
    }
  }

  // Helpers for settings builder structure
  const fieldsPath = 'forms.serviceRequest.fields'
  const ensureFields = () => setSettings(prev => ({ ...prev, [fieldsPath]: prev[fieldsPath] || {} }))

  if (loading) return <div className="p-6">Loading...</div>
  if (!tenant) return <div className="p-6">Tenant not found</div>

  return (
    <div className="space-y-6">
      <PageHeader title={tenant.name} description={`Slug: ${tenant.slug} · Created: ${new Date(tenant.created_at).toLocaleString()} · Plan: ${tenant.plan_name}`}>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/tenants')}>Back</Button>
          <Button variant="outline" onClick={toggleStatus}>{tenant.status === 'active' ? 'Suspend' : 'Activate'}</Button>
        </div>
      </PageHeader>

      {/*<Card>
        <CardHeader><CardTitle>Plan</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3 items-center">
            <div>
              <Label>Assigned Plan</Label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger className="w-full mt-1 bg-background text-foreground"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-6 md:mt-0">
              <Button onClick={savePlan} disabled={savingPlan || !planId}>Save Plan</Button>
            </div>
          </div>
        </CardContent>
      </Card>*/}

      <Card>
        <CardHeader><CardTitle>Roles & Users</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">Roles: {tenant.roles_count || 0} · Users: {tenant.users_count || 0}</div>
            <div className="space-x-2">
              <Link to={`/tenants/${tenantId}/roles`}><Button variant="outline">Manage Roles</Button></Link>
              <Link to={`/tenants/${tenantId}/users`}><Button variant="outline">Manage Users</Button></Link>
              <Button variant="outline" onClick={async () => {
                try {
                  const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/seed-default-roles`), { method: 'POST' })
                  if (!res.ok) throw new Error('Failed to seed roles')
                  toast({ title: 'Default roles seeded' })
                  await load()
                } catch (e: any) {
                  toast({ title: 'Error', description: e.message || 'Failed to seed roles', variant: 'destructive' })
                }
              }}>Seed Default Roles</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <TenantAnalyticsDashboard
        tenantId={tenantId}
      />

      <Card>
        <CardHeader><CardTitle>Tenant Settings</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>SR Field: Title visible</Label>
              <div className="mt-1">
                <input type="checkbox" className="accent-current" checked={Boolean(settings['forms.serviceRequest.fields']?.title?.visible ?? true)} onChange={(e) => {
                  ensureFields()
                  setSettings(prev => ({ ...prev, [fieldsPath]: { ...prev[fieldsPath], title: { ...(prev[fieldsPath]?.title || {}), visible: e.target.checked } } }))
                }} />
              </div>
            </div>
            <div>
              <Label>SR Field: Title required</Label>
              <div className="mt-1">
                <input type="checkbox" className="accent-current" checked={Boolean(settings['forms.serviceRequest.fields']?.title?.required ?? true)} onChange={(e) => {
                  ensureFields()
                  setSettings(prev => ({ ...prev, [fieldsPath]: { ...prev[fieldsPath], title: { ...(prev[fieldsPath]?.title || {}), required: e.target.checked } } }))
                }} />
              </div>
            </div>
            <div>
              <Label>Workflow Statuses (comma-separated)</Label>
              <Input className="mt-1 bg-background text-foreground" value={(settings['workflows.statuses'] || []).join?.(', ') || ''} onChange={(e) => setSettings(prev => ({ ...prev, ['workflows.statuses']: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={saveSettings}>Save Settings</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Features (Tenant Overrides)</CardTitle></CardHeader>
        <CardContent>
          {!planId ? (
            <div className="text-sm text-red-500">Assign a plan to enable feature configuration.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-muted-foreground">Plan default vs override; Effective shows runtime.</div>
                <Button variant="outline" size="sm" onClick={() => resetFeatures()}>Reset to defaults</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {featureList.map((key) => {
                  const defaultOn = !!featureDefaults[key]
                  const override = featureOverrides[key]
                  const effective = featureEffective[key]
                  return (
                    <div key={key} className="border border-border rounded p-2 bg-background text-foreground">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Checkbox checked={override ?? defaultOn} onCheckedChange={(v) => setFeatureOverrides(prev => ({ ...prev, [key]: Boolean(v) }))} />
                          <span className="capitalize text-foreground">{featureRegistry.find(f => f.key === key)?.name || key}</span>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded border border-border">Effective: {effective ? 'On' : 'Off'}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Plan default: {defaultOn ? 'On' : 'Off'} · Override: {override === undefined ? '—' : (override ? 'On' : 'Off')}
                        <Button variant="ghost" size="sm" className="ml-2 h-6 px-2" onClick={() => resetFeatures([key])}>Reset</Button>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="mt-4">
                <Card>
                  <CardHeader><CardTitle>Feature Limits (Tenant Overrides)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid gap-3">
                      {featureRegistry.filter(r => r.limitsSchema && Object.keys(r.limitsSchema).length > 0).map((r) => (
                        <div key={r.key} className="border border-border rounded p-2 bg-background text-foreground">
                          <div className="font-medium text-sm mb-1">{r.name || r.key}</div>
                          <div className="grid grid-cols-1 gap-2">
                            {Object.entries(r.limitsSchema || {}).map(([limitKey, meta]) => (
                              <div key={limitKey} className="text-xs">
                                <Label className="text-xs">{limitKey}</Label>
                                <Input
                                  className="mt-1 bg-background text-foreground"
                                  placeholder={meta.description || limitKey}
                                  value={String((featureLimits[r.key]?.[limitKey] ?? ''))}
                                  onChange={(e) => {
                                    const v = e.target.value
                                    setFeatureLimits(prev => ({
                                      ...prev,
                                      [r.key]: {
                                        ...(prev[r.key] || {}),
                                        [limitKey]: meta.type === 'number' ? (v === '' ? null : Number(v)) : (v === '' ? null : v)
                                      }
                                    }))
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-4">
                <Button onClick={saveFeatures} disabled={savingFeatures}>Save Features</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Domains</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-3 text-sm text-muted-foreground">
            Storage usage: {(storageUsage.total_storage_bytes / 1024 / 1024).toFixed(2)} MB · Files: {storageUsage.total_files_count}
          </div>
          <div className="space-y-2">
            {domains.map(d => (
              <div key={d.id} className="flex items-center justify-between border border-border rounded p-2 bg-background text-foreground">
                <div>
                  <div className="font-medium text-foreground">{d.domain}</div>
                  <div className="text-xs text-muted-foreground">{d.is_primary ? 'Primary' : 'Secondary'} · {d.is_verified ? 'Verified' : 'Unverified'}</div>
                </div>
                <div className="space-x-2">
                  {!d.is_primary && <Button variant="outline" size="sm" onClick={() => setPrimaryDomain(d.id)}>Set Primary</Button>}
                  {!d.is_verified && <Button variant="outline" size="sm" onClick={async () => {
                    const res = await authFetch(apiUrl(`/api/admin/tenants/${tenantId}/domains/${d.id}/verify`), { method: 'POST' })
                    if (!res.ok) { toast({ title: 'Error', description: 'Failed to verify', variant: 'destructive' }); return }
                    toast({ title: 'Domain verified' }); await load();
                  }}>Mark Verified</Button>}
                  <Button variant="outline" size="sm" onClick={() => deleteDomain(d.id)}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-2">
            <Input placeholder="Add domain (e.g. app.tenant.com)" className="bg-background text-foreground" onKeyDown={(e) => {
              const target = e.target as HTMLInputElement
              if (e.key === 'Enter' && target.value) { addDomain(target.value); target.value = ''; }
            }} />
            <Button onClick={() => {
              const input = document.querySelector<HTMLInputElement>('input[placeholder^="Add domain"]');
              if (input && input.value) { addDomain(input.value); input.value = ''; }
            }}>Add Domain</Button>
          </div>
        </CardContent>
      </Card>

    </div>
  )
}


